/** The tour screen's map layer.
 *
 * Lives in the PARENT document, not in the preview iframe. The frame is
 * `sandbox="allow-same-origin"` with no `allow-scripts`, so the Maps JS API cannot load
 * inside it; adding `allow-scripts` alongside `allow-same-origin` is equivalent to no
 * sandbox at all. Instead `DeviceFrame` renders this as an underlay inside the same
 * scaled, clipped box as the iframe — so the device transform, the rounded corners and
 * the overflow clip all apply to the map for free — and the frame's map region is
 * transparent and `pointer-events: none` so drags land here.
 *
 * Markers are `OverlayView`s rather than Maps marker icons so they can be styled with
 * the SAME generated Android colour tokens the rest of the renderer uses. Those tokens
 * are defined on the FRAME document's `:root`, so they are injected into the parent once
 * as well — they are all `--and-` prefixed, so nothing of the console's can collide.
 *
 * Upstream: ui/my_trips_details/details/TourDetailsScreenStateMapper.kt:283-360 (markers),
 * :655 (KML), TourDetailsScreen.kt:113-128 (camera + padding).
 */
import { GoogleMap, OverlayViewF, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { config } from '@/config';

import colorsCss from '../tokens/colors.css?inline';
import { MARKER_ANCHOR, pinFace } from './pins';
import { slotDisplayIndex, type TourDay } from '../adapters/tour';

/** `SINGLE_ACTIVITY_ZOOM_LEVEL = 15f` — a one-marker day cannot be fit to bounds. */
const SINGLE_ACTIVITY_ZOOM = 15;

/** Tokens live on the frame document; the overlay markers are in the parent. */
function useAndroidTokensInParent() {
  useEffect(() => {
    if (document.head.querySelector('style[data-preview-tokens]')) return;
    const el = document.createElement('style');
    el.setAttribute('data-preview-tokens', '');
    el.textContent = colorsCss;
    document.head.appendChild(el);
  }, []);
}

export interface MapMarker {
  /** tour-activity id — what upstream keys selection on */
  id: number;
  name: string;
  position: { lat: number; lng: number };
  slotId: number;
  face: ReturnType<typeof pinFace>;
  selected: boolean;
}

/** `mapMarkers` — the current day only, one marker per activity that HAS coordinates.
 *
 * The selected-slot branch is upstream's cluster expansion: when the selected activity's
 * slot holds more than one coordinate-bearing activity, the map shows ONLY that slot's
 * activities. Otherwise every slot in the day contributes. */
export function buildMarkers(day: TourDay | undefined, selectedActivityId: number | null): MapMarker[] {
  if (!day) return [];

  const selectedSlot =
    selectedActivityId === null
      ? undefined
      : day.timeslots.find((s) => s.activities.some((a) => a.id === selectedActivityId));
  const expand =
    selectedSlot !== undefined && selectedSlot.activities.filter((a) => a.position).length > 1;

  const slots = expand && selectedSlot ? [selectedSlot] : day.timeslots;

  const out: MapMarker[] = [];
  for (const slot of slots) {
    const index = slotDisplayIndex(day, slot);
    for (const activity of slot.activities) {
      if (!activity.position) continue;
      out.push({
        id: activity.id,
        name: activity.name,
        position: activity.position,
        slotId: slot.id,
        face: pinFace(slot.pin, index ?? 0),
        selected: activity.id === selectedActivityId,
      });
    }
  }
  return out;
}

export interface TourMapProps {
  day: TourDay | undefined;
  selectedActivityId: number | null;
  onSelect: (activityId: number | null) => void;
  /** Bottom inset the sheet covers, so fitted bounds stay in the visible strip. */
  sheetTopPx: number;
  toolbarPx: number;
}

export function TourMap({ day, selectedActivityId, onSelect, sheetTopPx, toolbarPx }: TourMapProps) {
  useAndroidTokensInParent();
  const apiKey = config.googleMapsApiKey;
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'console-google-maps',
    googleMapsApiKey: apiKey ?? '',
  });

  const markers = useMemo(() => buildMarkers(day, selectedActivityId), [day, selectedActivityId]);
  const mapRef = useRef<google.maps.Map | null>(null);

  // One map instance for the life of the pane: Maps JS bills per map load, and the
  // preview re-renders on every Render click.
  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);
  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // Camera. Upstream animates to the bounds of whatever markers are showing, with the
  // map padded by the toolbar above and the sheet below.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || markers.length === 0) return;
    if (markers.length === 1) {
      map.setCenter(markers[0]!.position);
      map.setZoom(SINGLE_ACTIVITY_ZOOM);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    for (const m of markers) bounds.extend(m.position);
    map.fitBounds(bounds, { top: toolbarPx + 16, bottom: sheetTopPx + 16, left: 16, right: 16 });
  }, [markers, sheetTopPx, toolbarPx]);

  if (!apiKey) {
    return <MapFallback text="Map not shown — VITE_GOOGLE_MAPS_API_KEY is not set." />;
  }
  if (loadError) return <MapFallback text="The map failed to load." />;
  if (!isLoaded) return <MapFallback text="Loading the map…" />;

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={() => onSelect(null)}
      center={markers[0]?.position ?? { lat: 0, lng: 0 }}
      zoom={markers.length === 1 ? SINGLE_ACTIVITY_ZOOM : 2}
      options={{
        disableDefaultUI: true,
        gestureHandling: 'greedy',
        clickableIcons: false,
      }}
    >
      {markers.map((marker) => (
        <OverlayViewF
          key={`${marker.slotId}_${marker.id}`}
          position={marker.position}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={(w, h) => ({
            x: -(w * MARKER_ANCHOR.x),
            y: -(h * MARKER_ANCHOR.y),
          })}
        >
          <Pin marker={marker} onSelect={onSelect} />
        </OverlayViewF>
      ))}
    </GoogleMap>
  );
}

/** The map region when there is no map to show. Deliberately reads as information —
 *  a console without a Maps key is a normal configuration, not a broken one. */
function MapFallback({ text }: { text: string }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 104,
        background: 'var(--and-color-surface)',
      }}
    >
      <div
        style={{
          maxWidth: 260,
          textAlign: 'center',
          fontFamily: 'Roboto, system-ui, sans-serif',
          fontSize: 12,
          color: 'var(--and-color-color-dark-medium)',
        }}
      >
        {text}
      </div>
    </div>
  );
}

/** `ic_uikit_pin_base` + a 20dp icon at anchor (0.5, 0.35).
 *
 *  The teardrop is hand-drawn — see the note in pins.ts. Colours come from the generated
 *  Android tokens, so they track `gen:renderer:android:tokens` rather than being copied. */
function Pin({ marker, onSelect }: { marker: MapMarker; onSelect: (id: number | null) => void }) {
  const { face, selected } = marker;
  const body = selected ? 'var(--and-color-blue)' : 'var(--and-color-white)';
  const ink = selected ? 'var(--and-color-white)' : 'var(--and-color-gray-3)';

  return (
    <button
      type="button"
      // Retap deselects, which is upstream's behaviour.
      onClick={() => onSelect(selected ? null : marker.id)}
      title={marker.name}
      aria-label={marker.name}
      style={{
        position: 'relative',
        width: 34,
        height: 44,
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        display: 'block',
      }}
    >
      <svg width="34" height="44" viewBox="0 0 34 44" aria-hidden="true">
        <path
          d="M17 43C17 43 32 26.5 32 16.5C32 8.2 25.3 1.5 17 1.5C8.7 1.5 2 8.2 2 16.5C2 26.5 17 43 17 43Z"
          fill={body}
          stroke="var(--and-color-divider)"
          strokeWidth="1"
        />
      </svg>
      <span
        style={{
          position: 'absolute',
          left: '50%',
          top: '35%',
          transform: 'translate(-50%, -50%)',
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Roboto, system-ui, sans-serif',
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1,
          color: ink,
          overflow: 'hidden',
        }}
      >
        {face.kind === 'text' && face.text}
        {face.kind === 'image' && (
          <img src={face.url} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
        )}
        {face.kind === 'icon' && (
          // The bundled glyphs are not exported yet; show the key's initial and mark an
          // unrecognised key, since falling back to `meeting_point` silently is exactly
          // the editorial mistake this preview exists to surface.
          <span style={{ fontSize: 10, textTransform: 'uppercase' }} title={face.key}>
            {face.recognised ? face.key.slice(0, 1) : '?'}
          </span>
        )}
      </span>
    </button>
  );
}
